import {
  EventDtoOriginEnum,
  EventDtoTypeEnum,
  EventHouseholdSummaryDto,
} from 'src/Services/API/generated';

export const HOUSEHOLD_PALETTE = [
  '#9AAB9E',
  '#C5A065',
  '#8FB3B8',
  '#B8A1C4',
  '#C7D8DA',
  '#D9B79A',
] as const;

export const FALLBACK_HOUSEHOLD_COLOR = HOUSEHOLD_PALETTE[0];

export const FILTER_GROUPS = {
  sejours: [EventDtoTypeEnum.Occupation, EventDtoTypeEnum.Location],
  maison: [
    EventDtoTypeEnum.Entretien,
    EventDtoTypeEnum.Work,
    EventDtoTypeEnum.EndOfContract,
    EventDtoTypeEnum.Activities,
    EventDtoTypeEnum.Other,
  ],
  reperes: [
    EventDtoTypeEnum.PublicHoliday,
    EventDtoTypeEnum.SchoolHolidayZoneA,
    EventDtoTypeEnum.SchoolHolidayZoneB,
    EventDtoTypeEnum.SchoolHolidayZoneC,
  ],
} as const satisfies Record<string, EventDtoTypeEnum[]>;

export type FilterGroupKey = keyof typeof FILTER_GROUPS;

export type EventVisualIcon =
  | 'IconTool'
  | 'IconHammer'
  | 'IconKey'
  | 'IconFileText'
  | 'IconConfetti'
  | 'IconNote'
  | 'IconBell';

export type EventVisualKind = 'ribbon' | 'chip' | 'reminder' | 'marker';

export interface EventVisual {
  kind: EventVisualKind;
  color?: string;
  icon?: EventVisualIcon;
  label: string;
  initial?: string;
  markerType?: EventDtoTypeEnum;
}

const MARKER_TYPES: readonly EventDtoTypeEnum[] = [
  EventDtoTypeEnum.PublicHoliday,
  EventDtoTypeEnum.SchoolHolidayZoneA,
  EventDtoTypeEnum.SchoolHolidayZoneB,
  EventDtoTypeEnum.SchoolHolidayZoneC,
];

const RIBBON_TYPES: readonly EventDtoTypeEnum[] = [
  EventDtoTypeEnum.Occupation,
  EventDtoTypeEnum.Location,
];

const CHIP_ICONS: Partial<Record<EventDtoTypeEnum, EventVisualIcon>> = {
  [EventDtoTypeEnum.Entretien]: 'IconTool',
  [EventDtoTypeEnum.Work]: 'IconHammer',
  [EventDtoTypeEnum.EndOfContract]: 'IconFileText',
  [EventDtoTypeEnum.Activities]: 'IconConfetti',
  [EventDtoTypeEnum.Other]: 'IconNote',
};

const initialOf = (value: string | null | undefined): string | undefined =>
  value && value.length > 0 ? value.charAt(0).toUpperCase() : undefined;

export interface EventVisualInput {
  type: EventDtoTypeEnum;
  origin: EventDtoOriginEnum;
  name: string;
  household?: EventHouseholdSummaryDto | null;
}

export const getEventVisual = (event: EventVisualInput): EventVisual => {
  if (event.origin === EventDtoOriginEnum.PracticalInformation) {
    return { kind: 'reminder', icon: 'IconBell', label: event.name };
  }

  if (MARKER_TYPES.includes(event.type)) {
    return { kind: 'marker', label: event.name, markerType: event.type };
  }

  if (RIBBON_TYPES.includes(event.type)) {
    const householdName = event.household?.name ?? null;
    return {
      kind: 'ribbon',
      color: event.household?.color ?? FALLBACK_HOUSEHOLD_COLOR,
      label: householdName ?? event.name,
      initial: initialOf(householdName) ?? initialOf(event.name),
    };
  }

  return {
    kind: 'chip',
    icon: CHIP_ICONS[event.type] ?? 'IconNote',
    label: event.name,
  };
};

export const groupToTypes = (group: FilterGroupKey): readonly EventDtoTypeEnum[] =>
  FILTER_GROUPS[group];

export const typeToGroup = (type: EventDtoTypeEnum): FilterGroupKey | null => {
  for (const key of Object.keys(FILTER_GROUPS) as FilterGroupKey[]) {
    const types: readonly EventDtoTypeEnum[] = FILTER_GROUPS[key];
    if (types.includes(type)) return key;
  }
  return null;
};
